/**
 * MeetingTranscriptViewer — lazy-fetched transcript panel.
 * Rendered inline inside MeetingDetailPanel; fetch is gated by `open` prop.
 */

import { FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useMeetingTranscript } from '../hooks/useMeetings';
import type { TranscriptSegment } from '../types';

interface MeetingTranscriptViewerProps {
  meetingId: string;
  hasTranscript: boolean;
}

function SegmentRow({ seg }: { seg: TranscriptSegment }) {
  const formatTime = (s: number) => {
    const mm = Math.floor(s / 60).toString().padStart(2, '0');
    const ss = Math.floor(s % 60).toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  return (
    <div className="flex gap-3 py-1.5 border-b border-border/50 last:border-0">
      <span className="shrink-0 font-mono text-xs text-muted-foreground w-10 pt-0.5">
        {formatTime(seg.start)}
      </span>
      <div className="flex-1 min-w-0">
        {seg.speaker && (
          <p className="text-xs font-semibold text-muted-foreground mb-0.5">{seg.speaker}</p>
        )}
        <p className="text-sm break-words">{seg.text}</p>
      </div>
    </div>
  );
}

export function MeetingTranscriptViewer({ meetingId, hasTranscript }: MeetingTranscriptViewerProps) {
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError } = useMeetingTranscript(meetingId, open);

  if (!hasTranscript) {
    return (
      <p className="text-sm text-muted-foreground">No transcript yet</p>
    );
  }

  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        <FileText className="h-4 w-4" />
        {open ? 'Hide transcript' : 'Show transcript'}
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
      </button>

      {open && (
        <div className="rounded-md border border-border bg-muted/30 p-3 max-h-80 overflow-y-auto">
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-5 rounded bg-muted animate-pulse" />
              ))}
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive">Failed to load transcript. Try again.</p>
          ) : !data ? (
            <p className="text-sm text-muted-foreground">Transcript processing…</p>
          ) : data.segments && data.segments.length > 0 ? (
            <div>
              {data.segments.map((seg, i) => (
                <SegmentRow key={i} seg={seg} />
              ))}
            </div>
          ) : (
            <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{data.text}</p>
          )}
        </div>
      )}
    </div>
  );
}
